import {Box, BoxProps} from '@mui/material';
import {useTranslation} from 'tolgee';

import {formatChipProbability, textShortener} from '~utils/miscUtils';
import {LabelSource} from '~redux/types/images';

import {curationChipName} from '~components/common/images/curationChip.utils';
import {MaddoxAvatar} from '~components/common/MaddoxAvatar';
import {FlashIcon, MaddoxLogoMonoIcon, SparklesMonoIcon, SparklesSmallIcon} from 'src/icons';

export type ThumbnailChipSource = 'human' | 'prediction' | 'synthetic' | 'timeout';
export type PredictionChipSize = 'thumbnail' | 'annotator';

const CHIP_DIMENSIONS = {
  thumbnail: {
    height: 24,
    avatarSize: 18,
    avatarFontSize: 8,
    dotSize: 8,
    sparklesSize: 12,
    flashSize: 16,
    logoSize: 22,
    probabilityHeight: 16,
    probabilityFontSize: '10px',
    probabilityLineHeight: '12px',
    labelFontSize: '12px',
  },
  annotator: {
    height: 32,
    avatarSize: 22,
    avatarFontSize: 10,
    dotSize: 8,
    sparklesSize: 18,
    flashSize: 16,
    logoSize: 22,
    probabilityHeight: 20,
    probabilityFontSize: '10px',
    probabilityLineHeight: '12px',
    labelFontSize: '12px',
  },
} as const;

export interface PredictionChipData {
  labelId: string;
  alias: string;
  color: string;
  condition?: Condition;
  probability?: number;
  isMachinePrediction?: boolean;
  isDefaultPrediction?: boolean;
  modelName?: string;
  source?: LabelSource;
  userEmail?: string;
  /** Set for evaluation predictions flagged as unstable by enhanced testing; renders the synthetic (sparkles) chip. */
  unstable?: boolean;
}

/**
 * Maps the backend label `source` to the chip's visual source (CE-2342/CE-2372).
 * `human` is the only annotation; curation predictions keep the synthetic (sparkles) treatment;
 * every other (or unknown) source defaults to a model prediction.
 */
export function chipSourceFromLabelSource(source: LabelSource | undefined): ThumbnailChipSource {
  switch (source) {
    case 'human':
      return 'human';
    case 'curation_image_selection':
    case 'curation_image_cleanup':
      return 'synthetic';
    default:
      return 'prediction';
  }
}
interface PredictionChipProps extends Omit<BoxProps, 'color'> {
  label: PredictionChipData;
  showProbability?: boolean;
  showProbabilityPercentSuffix?: boolean;
  showModelName?: boolean;
  isHighlighted?: boolean;
  chipSize?: PredictionChipSize;
}

export function resolveChipSource(label: PredictionChipData): ThumbnailChipSource {
  if (label.labelId === 'MODEL_TIMEOUT') {
    return 'timeout';
  }
  if (label.unstable) {
    // Unstable enhanced-testing predictions get the synthetic (sparkles) treatment regardless of their source.
    return 'synthetic';
  }
  return chipSourceFromLabelSource(label.source);
}

export function ThumbnailChipIcon({
  label,
  small = false,
  isHighlighted,
  chipSize = 'thumbnail',
}: {
  label: PredictionChipData;
  small?: boolean;
  isHighlighted?: boolean;
  chipSize?: PredictionChipSize;
}) {
  const chipSource = resolveChipSource(label);
  const dimensions = CHIP_DIMENSIONS[chipSize];

  if (chipSource === 'human') {
    if (label.userEmail && !small) {
      return (
        <MaddoxAvatar
          user={label.userEmail}
          color={label.color}
          isHighlighted={isHighlighted}
          width={dimensions.avatarSize}
          height={dimensions.avatarSize}
          sx={{
            '& .MuiAvatar-root': {
              width: dimensions.avatarSize,
              height: dimensions.avatarSize,
              fontSize: dimensions.avatarFontSize,
            },
          }}
        />
      );
    }
    return (
      <Box
        sx={{
          width: dimensions.dotSize,
          height: dimensions.dotSize,
          borderRadius: '50%',
          backgroundColor: label.color,
          flexShrink: 0,
        }}
      />
    );
  }

  if (chipSource === 'synthetic') {
    const iconSize = small ? CHIP_DIMENSIONS.thumbnail.sparklesSize : dimensions.sparklesSize;
    return (
      <Box
        sx={{
          display: 'flex',
          flexShrink: 0,
          width: iconSize,
          height: iconSize,
          '& svg': {width: iconSize, height: iconSize},
          color: label.color,
        }}
      >
        {small ? <SparklesSmallIcon /> : <SparklesMonoIcon />}
      </Box>
    );
  }

  if (chipSource === 'timeout') {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          width: dimensions.flashSize,
          height: dimensions.flashSize,
          '& svg': {width: dimensions.flashSize, height: dimensions.flashSize},
          color: label.color,
        }}
      >
        <FlashIcon />
      </Box>
    );
  }

  const logoSize = small ? 12 : dimensions.logoSize;
  return (
    <Box
      sx={{
        display: 'flex',
        flexShrink: 0,
        color: label.color,
        width: logoSize,
        height: logoSize,
        '& svg': {width: logoSize, height: logoSize},
      }}
    >
      <MaddoxLogoMonoIcon />
    </Box>
  );
}

/**
 * A compact chip component for displaying prediction labels in thumbnails.
 * Shows a colored left border, label name, and optional probability badge.
 */
export function PredictionChip({
  label,
  showProbability = true,
  showProbabilityPercentSuffix = true,
  showModelName = false,
  isHighlighted,
  chipSize = 'thumbnail',
  sx,
  ...rest
}: PredictionChipProps) {
  const {t} = useTranslation('review');
  const {alias, color, probability, modelName} = label;
  const chipSource = resolveChipSource(label);
  const curationName = chipSource === 'synthetic' ? curationChipName(t, label.labelId) : undefined;
  const displayLabel = curationName ?? alias;
  const hasProbability = showProbability && probability !== undefined && probability > 0;
  const dimensions = CHIP_DIMENSIONS[chipSize];

  return (
    <Box
      data-testid='prediction-chip'
      data-chip-source={chipSource}
      data-chip-size={chipSize}
      sx={[
        {
          display: 'inline-flex',
          alignSelf: 'flex-start',
          width: 'fit-content',
          maxWidth: '100%',
          height: dimensions.height,
          borderRadius: '4px',
          border: '1px solid',
          borderColor: color,
          paddingLeft: '4px',
          backgroundColor: color,
          typography: 'caption',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      {...rest}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          height: '100%',
          minWidth: 0,
          maxWidth: '100%',
          overflow: 'hidden',
          px: 0.5,
          backgroundColor: 'maddox.globalwhite',
          borderRadius: '4px 0 0 4px',
        }}
      >
        <ThumbnailChipIcon label={label} isHighlighted={isHighlighted} chipSize={chipSize} />
        <Box
          component='span'
          sx={{
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            fontSize: dimensions.labelFontSize,
            fontWeight: 600,
            color: 'maddox.black100',
          }}
        >
          {textShortener(displayLabel, 24)}
        </Box>
        {hasProbability && (
          <Box
            component='span'
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              flexShrink: 0,
              height: `${dimensions.probabilityHeight}px`,
              px: 0.5,
              fontSize: dimensions.probabilityFontSize,
              fontWeight: 500,
              lineHeight: dimensions.probabilityLineHeight,
              color: 'maddox.black60',
              backgroundColor: 'maddox.black5',
              borderRadius: '3px',
            }}
          >
            {formatChipProbability(probability, showProbabilityPercentSuffix)}
          </Box>
        )}
        {showModelName && modelName && (
          <Box
            component='span'
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              flexShrink: 0,
              height: `${dimensions.probabilityHeight}px`,
              px: 0.5,
              fontSize: dimensions.probabilityFontSize,
              fontWeight: 500,
              lineHeight: dimensions.probabilityLineHeight,
              color: 'maddox.secondary',
              backgroundColor: 'maddox.secondary5',
              borderRadius: '3px',
              textTransform: 'uppercase',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: '120px',
            }}
            title={modelName}
          >
            {modelName}
          </Box>
        )}
      </Box>
    </Box>
  );
}

interface OverflowChipProps extends BoxProps {
  count: number;
}

/**
 * A chip showing "+N" to indicate additional items that couldn't be displayed.
 */
export function OverflowChip({count, sx, ...rest}: OverflowChipProps) {
  return (
    <Box
      data-testid='overflow-chip'
      sx={[
        {
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: 24,
          px: 0.5,
          borderRadius: '4px',
          backgroundColor: 'maddox.globalwhite',
          border: '1px solid',
          borderColor: 'maddox.black10',
          typography: 'caption',
          fontSize: '12px',
          fontWeight: 500,
          color: 'maddox.black60',
          whiteSpace: 'nowrap',
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      {...rest}
    >
      +{count}
    </Box>
  );
}
